import React from "react";
import { Button } from "antd";
import Icons from "components/icons";
import { Localize } from "components/service";
import {
    formatStaffAttendanceDateTime,
    formatStaffAttendanceTime,
} from "../helpers/staff-attendance-api-helper";
import {
    canManuallyCheckoutStaffSession,
    getStaffAttendanceFailedCheckout,
    getStaffAttendanceSessions,
    getStaffAttendanceSessionStatus,
    getStaffManualCheckoutActor,
    isStaffSessionManuallyClosed,
} from "../helpers/staff-attendance-session-helper";
import { getStaffScanLocation } from "../helpers/staff-scan-location-helper";
import { getStaffAuditReason, getStaffAuditReasonDetail } from "../helpers/staff-audit-log-presentation-helper";


const getStatusIcon = (status) => {
    switch (status)
    {
        case "in_progress":
            return <Icons.StaffInProcess />;
        case "missed_checkout":
            return <Icons.StaffClock />;
        case "failed_checkout":
            return <Icons.StaffAttention />;
        case "completed":
            return <Icons.StaffLocationMap />;
        default:
            return null;
    }
};

const StaffAttendanceSessionDetailsField = ({ attendanceRow, onOpenLocation, manualCheckout }) => {
    const sessions = getStaffAttendanceSessions(attendanceRow);
    const showSessionNumber = sessions.length > 1;

    return (
        <div className={ `staff-attendance-session-details${ showSessionNumber ? " staff-attendance-session-details--multiple" : "" }` }>
            { sessions.map((session, index) => {
                const status = getStaffAttendanceSessionStatus(session, attendanceRow.status);
                const manuallyClosed = isStaffSessionManuallyClosed(session);
                const checkoutActor = getStaffManualCheckoutActor(session.check_out || session.relCheckOutLog);
                const canCheckout = !manuallyClosed && typeof manualCheckout?.onRequest === "function" &&
                    canManuallyCheckoutStaffSession(session);
                const checkoutPending = manualCheckout?.pendingSessionID !== null &&
                    manualCheckout?.pendingSessionID !== undefined;
                const checkoutDisabled = Boolean(manualCheckout?.disabled || checkoutPending);
                const failedCheckout = getStaffAttendanceFailedCheckout(session);
                const rejectionReason = getStaffAuditReason(failedCheckout?.lastAttempt?.reason);
                const rejectionDetail = getStaffAuditReasonDetail(failedCheckout?.lastAttempt);
                const location = getStaffScanLocation(
                    failedCheckout?.lastAttempt,
                    attendanceRow.worker
                );
                const zoneRadius = status === "failed_checkout" ?
                    failedCheckout?.lastAttempt?.zone_radius :
                    session.zone_radius;
                const isInsideZone = session.location_status === "inside_zone";
                const hasNoLocation = session.location_status === "no_location";
                const hasZoneRadius = (
                    zoneRadius !== null &&
                    zoneRadius !== undefined &&
                    Number.isFinite(Number(zoneRadius)) &&
                    Number(zoneRadius) > 0
                );

                return (
                    <div
                        key={ session.id || `session-${ index }` }
                        className={ `row-grid no-gap staff-attendance-session staff-attendance-session--${ status }${ manuallyClosed ? " staff-attendance-session--manual" : "" }` }>
                        <span>
                            { showSessionNumber ?
                                <Localize number={ index + 1 }>STAFF_MANAGEMENT.Text_SessionNumber</Localize> :
                                <Localize>STAFF_MANAGEMENT.Text_Session</Localize>
                            }
                        </span>
                        <span className="staff-attendance-session__time">
                            { formatStaffAttendanceTime(session.check_in_at) }
                        </span>
                        <span className="d-flex align-items-center staff-attendance-session__arrow" aria-hidden="true">
                            <Icons.Arrow />
                        </span>
                        <span className="staff-attendance-session__time">
                            { formatStaffAttendanceTime(session.check_out_at) }
                        </span>

                        <span className="d-flex align-items-center staff-attendance-session__state-icon" aria-hidden="true">
                            { manuallyClosed ? <Icons.StaffInfo /> : getStatusIcon(status) }
                        </span>

                        <div className="staff-attendance-session__content">
                            <span className="staff-attendance-session__state">
                                { manuallyClosed && (checkoutActor ?
                                    <Localize name={ checkoutActor }>STAFF_MANAGEMENT.ManualCheckout_By</Localize> :
                                    <Localize>STAFF_MANAGEMENT.ManualCheckout_UnknownActor</Localize>)
                                }
                                { !manuallyClosed && status === "completed" && isInsideZone && hasZoneRadius &&
                                    <Localize radius={ zoneRadius }>
                                        STAFF_MANAGEMENT.AttendanceDetails_AllScansInsideZone
                                    </Localize>
                                }
                                { !manuallyClosed && status === "completed" && isInsideZone && !hasZoneRadius &&
                                    <Localize>
                                        STAFF_MANAGEMENT.AttendanceDetails_AllScansInsideZoneUnknownRadius
                                    </Localize>
                                }
                                { !manuallyClosed && status === "completed" && hasNoLocation &&
                                    <Localize>STAFF_MANAGEMENT.Text_NoLocation</Localize>
                                }
                                { !manuallyClosed && status === "completed" && !isInsideZone && !hasNoLocation && <>&mdash;</> }
                                { !manuallyClosed && status === "in_progress" &&
                                    <Localize>STAFF_MANAGEMENT.AttendanceStatus_InProgress</Localize>
                                }
                                { !manuallyClosed && status === "missed_checkout" &&
                                    <Localize>STAFF_MANAGEMENT.AttendanceStatus_MissedCheckout</Localize>
                                }
                                { !manuallyClosed && status === "failed_checkout" && (
                                    rejectionReason === "outside_radius" && hasZoneRadius ?
                                        <Localize radius={ zoneRadius }>
                                            STAFF_MANAGEMENT.AttendanceDetails_CheckoutRejected
                                        </Localize> :
                                        rejectionReason === "unknown" ?
                                            <Localize>STAFF_MANAGEMENT.AttendanceDetails_CheckoutRejectedGeneric</Localize> :
                                            <Localize reason={
                                                <Localize worker={ rejectionDetail || "—" } name={ rejectionDetail }>
                                                    { rejectionReason === "manual_close" && !rejectionDetail ?
                                                        "STAFF_MANAGEMENT.ManualCheckout_UnknownActor" :
                                                        `STAFF_MANAGEMENT.AuditReason_${ rejectionReason }`
                                                    }
                                                </Localize>
                                            }>
                                                STAFF_MANAGEMENT.AttendanceDetails_CheckoutRejectedWithReason
                                            </Localize>
                                )
                                }
                                { !manuallyClosed && status === "unknown" && <>&mdash;</> }
                            </span>

                            { canCheckout &&
                                <Button
                                    size="small"
                                    className="staff-attendance-session__checkout"
                                    disabled={ checkoutDisabled }
                                    loading={ checkoutPending && String(manualCheckout.pendingSessionID) === String(session.id) }
                                    onClick={ (event) => {
                                        event.stopPropagation();
                                        if (!checkoutDisabled && canManuallyCheckoutStaffSession(session))
                                        {
                                            manualCheckout.onRequest(attendanceRow, session);
                                        }
                                    } }>
                                    <Localize>STAFF_MANAGEMENT.Action_ManualCheckout</Localize>
                                </Button>
                            }

                            <span className="staff-attendance-session__dates">
                                <span>
                                    <Localize dateTime={ formatStaffAttendanceDateTime(session.check_in_at) }>
                                        STAFF_MANAGEMENT.AttendanceDetails_CheckInAt
                                    </Localize>
                                </span>
                                <span>
                                    <Localize dateTime={ formatStaffAttendanceDateTime(session.check_out_at) }>
                                        STAFF_MANAGEMENT.AttendanceDetails_CheckOutAt
                                    </Localize>
                                </span>
                            </span>

                            { !manuallyClosed && status === "missed_checkout" &&
                                <span className="staff-attendance-session__note">
                                    <Localize>STAFF_MANAGEMENT.AttendanceDetails_MissedCheckoutNote</Localize>
                                </span>
                            }

                            { !manuallyClosed && status === "failed_checkout" && failedCheckout &&
                                <span className="staff-attendance-session__note">
                                    <Localize
                                        count={ failedCheckout.attemptsCount ?? "—" }
                                        first={ formatStaffAttendanceTime(failedCheckout.firstAttemptAt) }
                                        last={ formatStaffAttendanceTime(failedCheckout.lastAttemptAt) }
                                        distance={ failedCheckout.lastDistanceFromZone ?? "—" }>
                                        STAFF_MANAGEMENT.AttendanceDetails_FailedCheckoutSummary
                                    </Localize>
                                </span>
                            }

                            { !manuallyClosed && status === "failed_checkout" &&
                                (location ?
                                    <button
                                        type="button"
                                        className="btn-text-link btn-text-link--underline staff-attendance-session__map-link"
                                        onClick={ (event) => {
                                            event.stopPropagation();
                                            onOpenLocation?.(location);
                                        } }>
                                        <Localize>STAFF_MANAGEMENT.Action_ViewMap</Localize>
                                    </button> :
                                    <span className="staff-attendance-session__no-location">
                                        <Localize>STAFF_MANAGEMENT.Text_NoLocation</Localize>
                                    </span>
                                )
                            }
                        </div>
                    </div>
                );
            }) }
        </div>
    );
};

export default StaffAttendanceSessionDetailsField;
